import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface StockProduct {
  id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  current_stock: number;
  minimum_stock: number;
  cost_per_unit: number;
  price: number;
  category_id: string | null;
  categories?: {
    name: string;
  } | null;
}

export function useProductStock() {
  const { activeRestaurant } = useRestaurantContext();
  const [products, setProducts] = useState<StockProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchStockProducts = useCallback(async () => {
    if (!activeRestaurant) {
      setProducts([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("products")
        .select("id, name, description, image_url, current_stock, minimum_stock, cost_per_unit, price, category_id, categories(name)")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("tracks_stock", true)
        .eq("is_active", true)
        .order("name");

      if (error) throw error;

      setProducts(data as StockProduct[]);
    } catch (error) {
      console.error("Error fetching stock products:", error);
      toast.error("Erro ao carregar produtos com estoque");
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  const addProductStockMovement = useCallback(
    async (
      productId: string,
      type: "entry" | "exit" | "adjustment",
      quantity: number,
      reason?: string,
      cost?: number,
      expense?: {
        title: string;
        category_id: string;
        supplier_id?: string;
        due_date?: string;
      }
    ) => {
      if (!activeRestaurant) return;

      try {
        // Get authenticated user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          toast.error("Usuário não autenticado");
          return;
        }

        const product = products.find((p) => p.id === productId);
        if (!product) throw new Error("Produto não encontrado");

        const previousStock = product.current_stock;
        let newStock = previousStock;

        if (type === "entry") {
          newStock = previousStock + quantity;
        } else if (type === "exit") {
          newStock = previousStock - quantity;
        } else if (type === "adjustment") {
          newStock = quantity;
        }

        // Calculate costs
        const unitCost = type === "entry" && cost && quantity > 0 
          ? cost / quantity 
          : product.cost_per_unit;
        const totalCost = type === "entry" 
          ? (cost || 0) 
          : Math.abs(type === "adjustment" ? newStock - previousStock : quantity) * product.cost_per_unit;

        // Optional: create linked expense for entry movements with cost
        let expenseId: string | null = null;
        if (type === "entry" && expense && (cost || 0) > 0) {
          const today = new Date().toISOString().slice(0, 10);
          const { data: createdExpense, error: expenseError } = await supabase
            .from("expenses" as any)
            .insert({
              restaurant_id: activeRestaurant.id,
              title: expense.title,
              description: reason || null,
              amount: cost,
              status: "open",
              expense_date: today,
              due_date: expense.due_date || null,
              category_id: expense.category_id,
              supplier_id: expense.supplier_id || null,
              origin: "stock_entry",
              created_by: user.id,
            })
            .select("id")
            .single();
          if (expenseError) throw expenseError;
          expenseId = (createdExpense as unknown as { id?: string } | null)?.id || null;
        }

        // Create movement record with all required fields
        const { data: createdMovement, error: movementError } = await supabase
          .from("stock_movements" as any)
          .insert({
            restaurant_id: activeRestaurant.id,
            product_id: productId,
            type,
            quantity: type === "adjustment" ? newStock - previousStock : (type === "exit" ? -quantity : quantity),
            previous_stock: previousStock,
            new_stock: newStock,
            reason,
            cost: type === "entry" ? cost : null,
            unit_cost: unitCost,
            total_cost: totalCost,
            performed_by: user.id,
            expense_id: expenseId,
          });

        if (movementError) throw movementError;

        const movementId = (createdMovement as unknown as { id?: string } | null)?.id || null;
        if (expenseId && movementId) {
          await supabase
            .from("expenses" as any)
            .update({ linked_stock_movement_id: movementId })
            .eq("id", expenseId);
        }

        // Update product stock
        const updateData: any = { current_stock: newStock };
        
        // Update cost if provided on entry
        if (type === "entry" && cost !== undefined && cost > 0 && quantity > 0) {
          updateData.cost_per_unit = cost / quantity;
        }

        const { error: updateError } = await supabase
          .from("products")
          .update(updateData)
          .eq("id", productId);

        if (updateError) throw updateError;

        toast.success("Movimentação registrada com sucesso!");
        await fetchStockProducts();
      } catch (error) {
        console.error("Error adding product stock movement:", error);
        toast.error("Erro ao registrar movimentação");
      }
    },
    [activeRestaurant, products, fetchStockProducts]
  );

  const getLowStockProducts = useCallback(() => {
    return products.filter(
      (product) =>
        product.minimum_stock > 0 && product.current_stock < product.minimum_stock
    );
  }, [products]);

  const getTotalStockValue = useCallback(() => {
    return products.reduce(
      (sum, product) => sum + product.current_stock * product.cost_per_unit,
      0
    );
  }, [products]);

  return {
    products,
    isLoading,
    fetchStockProducts,
    addProductStockMovement,
    getLowStockProducts,
    getTotalStockValue,
  };
}
